import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, adminToken, camposAPI } from '../services/api';
import './AdminCampos.css';

function AdminCampos() {
  const navigate = useNavigate();
  const [campos, setCampos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampo, setEditingCampo] = useState(null);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    ubicacion: '',
    descripcion: '',
    num_hoyos: 18
  });

  const [hoyos, setHoyos] = useState([]);

  useEffect(() => {
    // El testigo guardado puede haber caducado: lo valida el servidor
    if (!adminToken.get()) {
      setLoading(false);
      return;
    }

    adminAPI.comprobarSesion()
      .then(response => {
        if (response.data.valida) {
          setIsAuthenticated(true);
          loadCampos();
        } else {
          adminToken.clear();
          setLoading(false);
        }
      })
      .catch(() => {
        adminToken.clear();
        setLoading(false);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoggingIn(true);

    try {
      const response = await adminAPI.login(loginData.username, loginData.password);
      adminToken.set(response.data.token);
      setIsAuthenticated(true);
      setLoading(true);
      loadCampos();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo conectar con el servidor');
    } finally {
      setLoggingIn(false);
    }
  };

  // Si el servidor rechaza la sesión, se vuelve al formulario de acceso
  const manejarError = (err, mensaje) => {
    console.error(mensaje, err);
    if (err.response?.status === 401 || err.response?.status === 503) {
      adminToken.clear();
      setIsAuthenticated(false);
      setError(err.response?.data?.error || 'La sesión ha caducado, vuelve a entrar');
      return;
    }
    setError(mensaje);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    adminToken.clear();
    navigate('/');
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const loadCampos = async () => {
    try {
      const response = await camposAPI.getAll();
      setCampos(response.data);
    } catch (err) {
      manejarError(err, 'Error al cargar los campos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'num_hoyos' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingCampo) {
        await camposAPI.update(editingCampo.id, formData);
      } else {
        const response = await camposAPI.create(formData);
        const campoId = response.data.id;

        // Crear hoyos automáticamente
        for (let i = 1; i <= formData.num_hoyos; i++) {
          await camposAPI.addHoyo(campoId, {
            numero: i,
            par: 4, // Par por defecto
            handicap: i, // Handicap por defecto
            distancia_metros: 350,
            descripcion: ''
          });
        }
      }

      setShowForm(false);
      setEditingCampo(null);
      resetForm();
      loadCampos();
    } catch (err) {
      manejarError(err, 'Error al guardar el campo');
    }
  };

  const handleEdit = (campo) => {
    setEditingCampo(campo);
    setFormData({
      nombre: campo.nombre,
      ubicacion: campo.ubicacion || '',
      descripcion: campo.descripcion || '',
      num_hoyos: campo.num_hoyos
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este campo?')) return;

    try {
      await camposAPI.delete(id);
      loadCampos();
    } catch (err) {
      manejarError(err, 'Error al eliminar el campo');
    }
  };

  const handleViewHoyos = async (campo) => {
    try {
      const response = await camposAPI.getHoyos(campo.id);
      setHoyos(response.data);
      setEditingCampo(campo);
    } catch (err) {
      manejarError(err, 'Error al cargar los hoyos');
    }
  };

  const handleUpdateHoyo = async (hoyoId, field, value) => {
    try {
      const hoyo = hoyos.find(h => h.id === hoyoId);
      const updatedHoyo = { ...hoyo, [field]: Number(value) };

      await camposAPI.updateHoyo(editingCampo.id, hoyoId, updatedHoyo);

      setHoyos(prev =>
        prev.map(h => (h.id === hoyoId ? { ...h, [field]: Number(value) } : h))
      );
    } catch (err) {
      manejarError(err, 'Error al actualizar el hoyo');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      ubicacion: '',
      descripcion: '',
      num_hoyos: 18
    });
    setEditingCampo(null);
    setHoyos([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-campos">
        <div className="header">
          <h1>🔒 Acceso Administrativo</h1>
          <p>Introduce tus credenciales</p>
        </div>

        <div className="container">
          <div className="card fade-in" style={{ maxWidth: '400px', margin: '0 auto' }}>
            {error && <div className="error">{error}</div>}

            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="username">Usuario</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={loginData.username}
                  onChange={handleLoginChange}
                  placeholder="admin"
                  required
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Contraseña</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  placeholder="••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={loggingIn}>
                {loggingIn ? 'Comprobando...' : 'Iniciar Sesión'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => navigate('/')}
                style={{ marginTop: '12px' }}
              >
                Volver
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando campos...</p>
      </div>
    );
  }

  return (
    <div className="admin-campos">
      <div className="header admin-header">
        <h1>Gestión de Campos</h1>
        <p>Administra los campos de golf</p>
        <button onClick={handleLogout} className="btn btn-danger admin-logout">
          Cerrar Sesión
        </button>
      </div>

      <div className="container">
        {error && <div className="error">{error}</div>}

        {!showForm && hoyos.length === 0 && (
          <>
            <button
              className="btn btn-primary btn-full"
              onClick={() => setShowForm(true)}
            >
              ➕ Nuevo Campo
            </button>

            <div className="campos-list">
              {campos.length === 0 ? (
                <div className="no-data">
                  <p>No hay campos creados todavía</p>
                </div>
              ) : (
                campos.map(campo => (
                  <div key={campo.id} className="campo-card">
                    <div className="campo-header">
                      <h3>{campo.nombre}</h3>
                      <span className="campo-hoyos">{campo.num_hoyos} hoyos</span>
                    </div>
                    {campo.ubicacion && (
                      <p className="campo-ubicacion">📍 {campo.ubicacion}</p>
                    )}
                    {campo.descripcion && (
                      <p className="campo-descripcion">{campo.descripcion}</p>
                    )}
                    <div className="campo-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleViewHoyos(campo)}
                      >
                        Ver Hoyos
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEdit(campo)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(campo.id)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              className="btn btn-secondary btn-full"
              onClick={() => navigate('/')}
              style={{ marginTop: '20px' }}
            >
              Volver al Inicio
            </button>
          </>
        )}

        {showForm && (
          <div className="card fade-in">
            <h2>{editingCampo ? 'Editar Campo' : 'Nuevo Campo'}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="nombre">Nombre del Campo</label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  placeholder="Ej: Club de Golf Las Lomas"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="ubicacion">Ubicación (opcional)</label>
                <input
                  type="text"
                  id="ubicacion"
                  name="ubicacion"
                  value={formData.ubicacion}
                  onChange={handleChange}
                  placeholder="Ej: Madrid, España"
                />
              </div>

              <div className="form-group">
                <label htmlFor="num_hoyos">Número de Hoyos</label>
                <select
                  id="num_hoyos"
                  name="num_hoyos"
                  value={formData.num_hoyos}
                  onChange={handleChange}
                  required
                  disabled={!!editingCampo}
                >
                  <option value="9">9 hoyos</option>
                  <option value="18">18 hoyos</option>
                </select>
                {editingCampo && (
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    No se puede cambiar el número de hoyos una vez creado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="descripcion">Descripción (opcional)</label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Breve descripción del campo..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-full">
                {editingCampo ? 'Actualizar' : 'Crear Campo'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                style={{ marginTop: '12px' }}
              >
                Cancelar
              </button>
            </form>
          </div>
        )}

        {hoyos.length > 0 && (
          <div className="card fade-in">
            <div className="hoyos-header">
              <h2>Hoyos de {editingCampo?.nombre}</h2>
              <button
                className="btn-close"
                onClick={() => {
                  setHoyos([]);
                  setEditingCampo(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="hoyos-table">
              <table>
                <thead>
                  <tr>
                    <th>Hoyo</th>
                    <th>Par</th>
                    <th>HCP</th>
                    <th>Metros</th>
                  </tr>
                </thead>
                <tbody>
                  {hoyos.map(hoyo => (
                    <tr key={hoyo.id}>
                      <td className="hoyo-numero">{hoyo.numero}</td>
                      <td>
                        <input
                          type="number"
                          value={hoyo.par}
                          onChange={(e) => handleUpdateHoyo(hoyo.id, 'par', e.target.value)}
                          min="3"
                          max="6"
                          className="hoyo-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={hoyo.handicap}
                          onChange={(e) => handleUpdateHoyo(hoyo.id, 'handicap', e.target.value)}
                          min="1"
                          max="18"
                          className="hoyo-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={hoyo.distancia_metros || 0}
                          onChange={(e) => handleUpdateHoyo(hoyo.id, 'distancia_metros', e.target.value)}
                          min="0"
                          className="hoyo-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              className="btn btn-secondary btn-full"
              onClick={() => {
                setHoyos([]);
                setEditingCampo(null);
              }}
              style={{ marginTop: '16px' }}
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCampos;
